import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../../api/client';
import { uploadFile } from '../../utils/upload';
import {
  Plus, Edit3, Trash2, ArrowLeft, Save, Image as ImageIcon,
  Users, ChevronUp, ChevronDown, Upload, Check
} from 'lucide-react';
import { toast, confirmDialog } from '../../components/Toast/toast';

const EMPTY_MEMBER = {
  name: '',
  role: '',
  description: '',
  photo_url: '',
  sort_order: 0,
};

export default function TeamAdmin() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [imgPreview, setImgPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [animating, setAnimating] = useState(null);
  const fileInputRef = useRef(null);

  const loadMembers = useCallback(() => {
    setLoading(true);
    client.get('/team/')
      .then(res => setMembers(res.data || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  // Сохранение нового порядка
  const saveOrder = async (reordered) => {
    try {
      await Promise.all(
        reordered.map((member, index) =>
          client.put(`/team/${member.id}`, { sort_order: index })
        )
      );
    } catch (err) {
      console.error('Ошибка сохранения порядка:', err);
    }
  };

  // Перемещение специалиста вверх/вниз с анимацией
  const moveMember = (index, direction) => {
    const sorted = [...members].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;
    if (animating) return;

    setAnimating({ movedId: sorted[index].id, targetId: sorted[targetIndex].id, direction });

    setTimeout(() => {
      setMembers(prev => {
        const arr = [...prev].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];
        // Обновляем sort_order чтобы порядок сохранился после рендера
        const updated = arr.map((m, i) => ({ ...m, sort_order: i }));
        saveOrder(updated);
        return updated;
      });
      setAnimating(null);
    }, 300);
  };

  const openAdd = () => {
    setEditing('new');
    setForm({ ...EMPTY_MEMBER });
    setImgPreview('');
  };

  const openEdit = (m) => {
    setEditing(m.id);
    setForm({
      name: m.name || '',
      role: m.role || '',
      description: m.description || '',
      photo_url: m.photo_url || '',
      sort_order: m.sort_order ?? 0,
    });
    setImgPreview(m.photo_url || '');
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_MEMBER);
    setImgPreview('');
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'photo_url') setImgPreview(value);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadFile(file);
      handleChange('photo_url', url);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message || 'Неизвестная ошибка';
      setError(`Ошибка загрузки фото: ${msg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.role.trim()) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editing === 'new') {
        await client.post('/team/', payload);
      } else {
        await client.put(`/team/${editing}`, payload);
      }
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        closeForm();
        loadMembers();
      }, 1200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message || 'Неизвестная ошибка';
      setError(`Ошибка сохранения: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog('Удалить специалиста?', {
      danger: true,
      okLabel: 'Удалить',
      cancelLabel: 'Отмена',
      detail: 'Специалист будет удалён навсегда из команды.',
      onConfirm: async () => {
        try {
          await client.delete(`/team/${id}`);
          loadMembers();
        } catch (err) {
          toast.error('Не удалось удалить специалиста');
          throw err;
        }
      },
    });
    if (!confirmed) return;
  };

  return (
    <div className="space-y-4">
      {/* Список — только когда редактор закрыт (форма не уезжает вниз и не модалка) */}
      {editing === null && (
      <>
      {/* Панель и кнопка добавления */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-[#64748b]">
          {members.length} {members.length === 1 ? 'специалист' : 'специалистов'}
        </h2>
        <button
          onClick={openAdd}
          className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#374151] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f2937] active:scale-[0.97]"
        >
          <Plus className="h-4 w-4" />
          Добавить
        </button>
      </div>

      {/* Список специалистов */}
      {loading ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          Загрузка...
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          <Users className="mx-auto mb-2 h-10 w-10 text-[#e2e8f0]" />
          <p>Специалисты не добавлены</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
          {[...members]
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map((m, index) => {
              // Определяем сдвиг для анимации
              let transform = 'translateY(0)';
              if (animating) {
                if (animating.movedId === m.id) {
                  transform = animating.direction === -1 ? 'translateY(-100%)' : 'translateY(100%)';
                } else if (animating.targetId === m.id) {
                  transform = animating.direction === -1 ? 'translateY(100%)' : 'translateY(-100%)';
                }
              }
              const isMoving = animating && (animating.movedId === m.id || animating.targetId === m.id);

              return (
                <div
                  key={m.id}
                  style={{
                    transform: isMoving ? transform : 'translateY(0)',
                    transition: isMoving ? 'transform 300ms ease-in-out' : 'none',
                    position: 'relative',
                    zIndex: isMoving ? 1 : 0,
                  }}
                  className="flex items-center gap-3 rounded-xl border border-[#e2e8f0] bg-white p-3 shadow-sm"
                >
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveMember(index, -1)}
                      disabled={index === 0}
                      className="grid h-5 w-6 place-items-center rounded text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-20"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => moveMember(index, 1)}
                      disabled={index === members.length - 1}
                      className="grid h-5 w-6 place-items-center rounded text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-20"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-green-sage to-green-primary">
                    {m.photo_url ? (
                      <img src={m.photo_url} alt={m.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-white text-xl font-bold">
                        {m.name?.[0] || '?'}
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-[#0f172a]">{m.name}</div>
                    <div className="mt-0.5 text-xs text-[#64748b]">{m.role}</div>
                    {m.description && (
                      <div className="mt-0.5 truncate text-xs text-[#64748b]">{m.description}</div>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => openEdit(m)}
                      className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] transition hover:bg-[#f1f5f9] hover:text-[#0f172a] active:scale-90"
                      aria-label="Редактировать"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] transition hover:bg-[#D64545]/10 hover:text-[#D64545] active:scale-90"
                      aria-label="Удалить"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      </>
      )}

      {/* Редактор специалиста — отдельной страницей (как «Новый товар») */}
      {editing !== null && (
        <div className="space-y-4">
          <header className="rounded-2xl border border-[#e2e8f0] bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <button onClick={closeForm} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#f1f5f9] text-[#64748b] transition active:scale-90">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-[#0f172a]">
                {editing === 'new' ? 'Новый специалист' : 'Редактирование'}
              </h2>
            </div>
          </header>
          <div className="rounded-2xl border border-[#e2e8f0] bg-white p-4">

            {error && (
              <div className="mb-4 rounded-lg bg-[#D64545]/10 p-3 text-sm text-[#D64545]">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Фото */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Фото</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  {imgPreview ? (
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-[#e2e8f0]">
                      <img
                        src={imgPreview}
                        alt="preview"
                        className="h-full w-full object-cover"
                        onError={() => setImgPreview('')}
                      />
                    </div>
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-green-sage to-green-primary text-white text-xl font-bold">
                      ?
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-2 text-xs font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95 disabled:opacity-50"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {uploading ? 'Загрузка...' : 'Выбрать фото'}
                    </button>
                    {imgPreview && (
                      <button
                        type="button"
                        onClick={() => { handleChange('photo_url', ''); setImgPreview(''); }}
                        className="text-xs text-[#D64545]/70 hover:text-[#D64545]"
                      >
                        Удалить фото
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Имя */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Имя *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => handleChange('name', e.target.value)}
                  placeholder="Имя специалиста"
                  className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>

              {/* Роль */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Роль *</label>
                <input
                  type="text"
                  value={form.role}
                  onChange={e => handleChange('role', e.target.value)}
                  placeholder="Должность / роль"
                  className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>

              {/* Описание */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Описание</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={e => handleChange('description', e.target.value)}
                  placeholder="Краткое описание"
                  className="w-full resize-none rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>
            </div>

            {/* Кнопки */}
            <div className="mt-5 flex gap-2">
              <button
                onClick={closeForm}
                className="flex-1 rounded-xl border border-[#e2e8f0] py-2.5 text-sm font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-[0.98]"
              >
                Отмена
              </button>
              <button
                onClick={handleSave}
                disabled={saving || saved || !form.name.trim() || !form.role.trim()}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold shadow-sm transition active:scale-[0.98] disabled:opacity-50 ${
                  saved
                    ? 'bg-[#6B9B74]/15 text-[#6B9B74]'
                    : 'bg-[#374151] text-white hover:bg-[#1f2937]'
                }`}
              >
                {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                {saved ? 'Сохранено!' : saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
